/**
 * Lift2Fit — the standalone chat workspace (`#/chat`, 0028 IA).
 *
 * Deliberately OUTSIDE the CRM shell: no workspace tabs, no coach nav. It is
 * its own small app, opened in a new window from the CRM header and from a
 * client's page, and it deep-links / refreshes on its own.
 *
 * Everything conversational lives here: every live relationship (paid,
 * connected, inquiry) with a tier badge, a tier filter, unread counts and the
 * connect/pack actions. **Prospects live here too** — the funnel is part of
 * talking to people, not a separate nav tab.
 *
 * The model is unchanged: chat is unlimited, connect is one coach, buy follows
 * connect, and no calendar exists before a pack.
 */
import { useEffect, useState } from 'react';
import {
  api,
  type CoachPackOwner,
  type ConnectionTier,
  type Trainee,
} from '@/lib/api';
import { cn, timeAgo } from '@/lib/format';
import { useSession } from '@/lib/useSession';
import { Button } from '@/components/ui';
import { AlertIcon, ArrowRightIcon, CheckIcon, CloseIcon, SparkIcon } from '@/lib/icons';
import { useAsync } from '../hooks';
import { EmptyState, LiveMessage, LoadingPanel, Panel, StatusChip } from '../components';
import { ChatAvatar, ChatIcon, CoachChatPanel } from '../chat';
import { Link } from '../router';
import { HelpConversation } from './HelpConversation';
import { HelpFeedbackPanel } from './HelpFeedbackPanel';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ChatContextPanel } from './ChatContextPanel';
import { LifterCoachCard } from './LifterCoachCard';

type Tone = 'info' | 'success' | 'danger';
type Filter = 'all' | 'paid' | 'connected' | 'inquiry' | 'managed';

/** The dropdown's wording. `managed` is an OWNERSHIP cut, not a tier. */
const FILTER_LABEL: Record<Filter, string> = {
  all: 'All',
  paid: 'Paid client',
  connected: 'Connected',
  inquiry: 'Inquiry',
  managed: 'Gym clients',
};

const TIER_LABEL: Record<ConnectionTier, string> = {
  paid: 'Paid client',
  connected: 'Connected',
  inquiry: 'Inquiry',
};

const TIER_TONE: Record<ConnectionTier, 'success' | 'info' | 'neutral'> = {
  paid: 'success',
  connected: 'info',
  inquiry: 'neutral',
};

/** The same pair-tier, worded for the reader: a coach's "paid client" is the
 * lifter's "pack active"; a coach's "inquiry" is the lifter's "chatting". */
const LIFTER_STATE_LABEL: Record<ConnectionTier, string> = {
  paid: 'Pack active',
  connected: 'Connected',
  inquiry: 'Chatting',
};

/** The client this window was opened on: `#/chat?clientId=<uuid>`. */
function preselectFromHash(): string | null {
  if (typeof window === 'undefined') return null;
  const query = window.location.hash.split('?')[1] ?? '';
  return new URLSearchParams(query).get('clientId');
}

/** The side this window was opened on: `#/chat?side=lifter` (coaches default). */
function sideFromHash(): 'coach' | 'lifter' | null {
  if (typeof window === 'undefined') return null;
  const query = window.location.hash.split('?')[1] ?? '';
  const side = new URLSearchParams(query).get('side');
  return side === 'lifter' || side === 'coach' ? side : null;
}

/**
 * Who owns this relationship. A coach can hold both kinds at once — their own
 * clients and clients a gym owns — so the chat labels it rather than making the
 * coach infer it from the tier (both can be "paid client").
 */
function OwnerChip({
  owned,
  ownerName,
  withName = false,
}: {
  owned: boolean;
  ownerName: string | null;
  /** The thread header has room for the gym's name; a list row does not. */
  withName?: boolean;
}) {
  if (owned) {
    return (
      <span className="whitespace-nowrap rounded-pill border border-hairline bg-surface px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-ink-muted">
        Your client
      </span>
    );
  }
  return (
    <span
      title={ownerName ? `Managed for ${ownerName}` : 'Managed for a gym'}
      className="max-w-[11rem] truncate whitespace-nowrap rounded-pill border border-accent bg-page-subtle px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-accent"
    >
      {withName && ownerName ? `Gym · ${ownerName}` : 'Gym client'}
    </span>
  );
}

function TierChip({ tier, isCoach }: { tier: ConnectionTier; isCoach: boolean }) {
  return (
    <StatusChip tone={TIER_TONE[tier]} className="whitespace-nowrap">
      {isCoach ? TIER_LABEL[tier] : LIFTER_STATE_LABEL[tier]}
    </StatusChip>
  );
}

/**
 * The coach's context panel data, fetched LAZILY the first time a coach selects
 * a thread and then held for the life of the window. One read of the existing
 * roster endpoint — no per-thread fetch, no refetch while typing, because the
 * effect is keyed on the boolean "has a thread been selected", not on the
 * selection or the draft.
 */
function useLazyCoachRoster(enabled: boolean): {
  rows: Trainee[] | null;
  error: string | null;
  reload: () => void;
} {
  const [state, setState] = useState<{ rows: Trainee[] | null; error: string | null }>({
    rows: null,
    error: null,
  });
  // Bumped only by an explicit `reload()` (0030 assignment), so typing or
  // switching threads never refetches the roster.
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    void api.coachClients().then((result) => {
      if (!alive) return;
      if (result.data) setState({ rows: result.data.data, error: null });
      else setState({ rows: [], error: result.problem?.detail ?? 'Could not read their programme.' });
    });
    return () => {
      alive = false;
    };
  }, [enabled, nonce]);
  return { ...state, reload: () => setNonce((value) => value + 1) };
}

export function ChatWorkspace() {
  const session = useSession();
  const isCoachViewer = session.hasRole('coach');
  // One workspace, two roles. The side is chosen by the entry point (a coach's
  // header sends the coach side; the lifter's dashboard sends `?side=lifter`)
  // and defaults to whatever role the account holds. The tier is the PAIR's, so
  // it is the same on both sides — only the affordances differ.
  const [side, setSide] = useState<'coach' | 'lifter'>(
    () => sideFromHash() ?? (isCoachViewer ? 'coach' : 'lifter'),
  );
  const isCoach = side === 'coach';
  const threadsState = useAsync(() => api.coachThreads(side), [side]);
  const packsState = useAsync(() => api.coachPacks(), []);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(() => preselectFromHash());
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [tone, setTone] = useState<Tone>('info');
  const [busy, setBusy] = useState(false);
  const [packId, setPackId] = useState('');
  // 0030: the owner can reassign a client from the workspace; an internal coach
  // gets no control (only the manager label in the context panel).
  const [assignId, setAssignId] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);
  const internalState = useAsync(() => api.internalCoaches(), []);
  // 0035: "Help" is a contact every account has, pinned at the TOP of the list
  // (above the filters, so no sorter can move or hide it). It is not a coaching
  // link, so it is local state, not a row in `coachThreads`.
  const [helpOpen, setHelpOpen] = useState(false);
  const helpUnread = useAsync(() => api.supportUnread(), []);

  const all = threadsState.data?.data ?? [];
  const byTier =
    filter === 'all'
      ? all
      : filter === 'managed'
        ? all.filter((thread) => !thread.owned)
        : all.filter((thread) => thread.tier === filter);
  const needle = search.trim().toLowerCase();
  // Search is a coach affordance only (the user: "coach can sort or search on
  // them, lifters having no such option").
  const filtered =
    isCoach && needle !== ''
      ? byTier.filter(
          (thread) =>
            thread.displayName.toLowerCase().includes(needle) ||
            thread.email.toLowerCase().includes(needle),
        )
      : byTier;
  // The requested client wins even if the current filter would hide them; else
  // the first row of the filter.
  const requested = all.find((thread) => thread.userId === selectedUserId) ?? null;
  const selected = requested ?? filtered[0] ?? null;
  const packs: CoachPackOwner[] = packsState.data ?? [];
  // The coach's context panel: lazy, cached for the window, and only for the
  // coach side. A lifter never reads the roster.
  const contextEnabled = isCoach && selected !== null;
  const rosterState = useLazyCoachRoster(contextEnabled);
  const contextRow = selected
    ? (rosterState.rows?.find((row) => row.userId === selected.userId) ?? null)
    : null;
  const contextLoading = contextEnabled && rosterState.rows === null;
  const ownerTeam =
    session.user?.internalCoachOf == null ? (internalState.data?.data ?? []) : [];

  useEffect(() => {
    setAssignId(contextRow?.managedBy?.kind === 'internal' ? contextRow.managedBy.coachId : '');
  }, [contextRow?.userId, contextRow?.managedBy?.kind, contextRow?.managedBy?.coachId]);

  const assignSelected = async () => {
    if (!selected) return;
    setAssignBusy(true);
    setNotice(null);
    const result = await api.assignClient(selected.userId, assignId === '' ? null : assignId);
    setAssignBusy(false);
    if (result.problem) {
      setTone('danger');
      setNotice(result.problem.detail ?? 'That assignment was refused.');
      return;
    }
    setTone('success');
    setNotice(
      result.data?.assignedCoachName
        ? `${selected.displayName} is now managed by ${result.data.assignedCoachName}.`
        : `${selected.displayName} is back with you.`,
    );
    rosterState.reload();
  };

  // Re-read the preselect if the window's hash changes (e.g. a second
  // `open in new window` from the same tab).
  useEffect(() => {
    const onHash = () => {
      const next = preselectFromHash();
      if (next) setSelectedUserId(next);
      const nextSide = sideFromHash();
      if (nextSide) setSide(nextSide);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (threadsState.loading && threadsState.data === null) {
    return (
      <div className="bg-page p-6" data-testid="chat-workspace">
        <LoadingPanel label="Loading your chatters" rows={5} />
      </div>
    );
  }

  if (threadsState.data === null) {
    return (
      <div className="bg-page p-6" data-testid="chat-workspace">
        <EmptyState
          title="We could not read your chats"
          body={threadsState.error ?? 'The API did not answer.'}
          icon={<AlertIcon className="h-8 w-8 text-accent" />}
        />
      </div>
    );
  }

  const counts: Record<Filter, number> = {
    all: all.length,
    paid: threadsState.data.paid,
    connected: threadsState.data.connected,
    inquiry: threadsState.data.inquiry,
    // Ownership is orthogonal to the tier: a gym's client can be paid or
    // connected, so this count is over the same list, not the API's tier counts.
    managed: all.filter((thread) => !thread.owned).length,
  };

  const decide = async (action: 'accept' | 'decline') => {
    if (!selected?.requestCode) return;
    setBusy(true);
    setTone('info');
    setNotice(action === 'accept' ? 'Connecting…' : 'Declining…');
    const result =
      action === 'accept'
        ? await api.acceptCoachInvite(selected.requestCode, undefined, draft)
        : await api.declineCoachInvite(selected.requestCode, draft);
    setBusy(false);
    if (result.problem || !result.data) {
      setTone('danger');
      setNotice(result.problem?.detail ?? 'That request could not be answered.');
      return;
    }
    setTone('success');
    setNotice(
      action === 'accept'
        ? `${selected.displayName} is connected. They stay an unpaid prospect until they take a pack.`
        : `${selected.displayName} was declined${draft.trim() ? ' and your note is on their request' : ''}.`,
    );
    setDraft('');
    threadsState.reload();
  };

  const offerPack = async () => {
    if (!selected || packId === '') return;
    setBusy(true);
    setTone('info');
    setNotice('Sending the pack offer…');
    const pack = packs.find((entry) => entry.packId === packId);
    const result = await api.inviteCoach({
      traineeEmail: selected.email,
      ...(pack ? { name: pack.name } : {}),
      packId,
    });
    setBusy(false);
    if (result.problem || !result.data) {
      setTone('danger');
      setNotice(result.problem?.detail ?? 'That pack offer could not be sent.');
      return;
    }
    setTone('success');
    setNotice(`Pack offer sent to ${selected.displayName}. Accepting it makes them a paid client.`);
    threadsState.reload();
  };

  // Lifter-side actions. A lifter does not accept/decline or sell; they cancel
  // an outstanding request or disconnect from a coach.
  const cancelRequest = async () => {
    if (!selected?.requestCode) return;
    setBusy(true);
    setTone('info');
    setNotice('Cancelling…');
    const result = await api.cancelCoachInvite(selected.requestCode);
    setBusy(false);
    if (result.problem || !result.data) {
      setTone('danger');
      setNotice(result.problem?.detail ?? 'That request could not be cancelled.');
      return;
    }
    setTone('success');
    setNotice(`Request to ${selected.displayName} cancelled. You can still send a new one.`);
    threadsState.reload();
  };

  const disconnect = async () => {
    if (!selected) return;
    setBusy(true);
    setTone('info');
    setNotice('Disconnecting…');
    const result = await api.revokeCoachLink(selected.userId);
    setBusy(false);
    if (result.problem || !result.data) {
      setTone('danger');
      setNotice(result.problem?.detail ?? 'That link could not be disconnected.');
      return;
    }
    setTone('success');
    setNotice(`Disconnected from ${selected.displayName}. You can connect to another coach now.`);
    setSelectedUserId(null);
    threadsState.reload();
  };

  return (
    <div className="l2f-chat-backdrop flex h-screen min-h-0 flex-col" data-testid="chat-workspace">
      {/* The workspace's own slim header — no CRM tabs, no coach nav. */}
      <header
        className="flex flex-wrap items-center justify-between gap-3 px-4 pb-3 pt-5 sm:px-6 sm:pt-6"
        data-testid="chat-header"
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-chat-lg border border-hairline bg-surface text-accent shadow-float">
            <ChatIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-muted">
              {isCoach ? 'Coaching workspace' : 'Your training'}
            </p>
            <h1 className="truncate font-display text-[22px] font-black uppercase leading-none tracking-[-0.01em] text-ink">
              {isCoach ? 'Chats & prospects' : 'Your coaches'}
            </h1>
          </div>
          {threadsState.data.unread > 0 ? (
            <span data-testid="chat-header-unread">
              <StatusChip tone="info">{threadsState.data.unread} unread</StatusChip>
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {/* 0034: the chat is a standalone window with no shell header, so the
              theme control has to live here or not exist at all. */}
          <ThemeToggle />
          <a
            href={isCoach ? '#/crm' : '#/dashboard'}
            data-testid="chat-back-home"
            className="inline-flex h-10 items-center gap-1.5 rounded-pill border border-hairline bg-surface px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink shadow-float transition-colors duration-150 hover:border-accent hover:text-accent"
          >
            Back to Home
            <ArrowRightIcon />
          </a>
        </div>
      </header>

      <div
        className={cn(
          'grid min-h-0 flex-1 gap-3 p-3 pt-0 sm:gap-4 sm:p-6 sm:pt-0 lg:grid-cols-[340px_minmax(0,1fr)]',
          /*
           * Three columns whenever there is a rail to put in the third one:
           * the coach's client context, the lifter's coach card, or Help's
           * review panel. Leaving the lifter out of this condition was the bug
           * that dropped their coach card BELOW the conversation instead of
           * beside it — the grid stayed two columns and the aside became a new
           * row.
           */
          selected !== null || helpOpen ? 'xl:grid-cols-[320px_minmax(0,1fr)_300px]' : '',
        )}
      >
        <aside className="flex min-h-[320px] flex-col lg:min-h-0">
          <Panel
            shape="chat"
            testId="chat-chatters"
            title={isCoach ? 'Chatters' : 'Coaches'}
            subtitle={
              isCoach ? `${all.length} live · prospects included` : `${all.length} conversation${all.length === 1 ? '' : 's'}`
            }
            className="min-h-0"
            bodyClassName="gap-2 min-h-0 max-h-[38vh] overflow-y-auto p-3 lg:max-h-none"
          >
            {/* 0035: the platform contact, always present and never filtered out. */}
            <button
              type="button"
              data-testid="chat-help-contact"
              aria-current={helpOpen}
              onClick={() => {
                setHelpOpen(true);
                setSelectedUserId(null);
              }}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-chat border px-3 py-2.5 text-left transition-all duration-150',
                helpOpen
                  ? 'border-accent bg-page-subtle shadow-float'
                  : 'border-hairline bg-surface hover:-translate-y-0.5 hover:border-accent hover:shadow-float',
              )}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-on-accent">
                  <ChatIcon className="h-4 w-4" />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-[14px] font-bold text-ink">Help</span>
                  <span className="truncate text-[11px] text-ink-muted">Ask the platform team</span>
                </span>
              </span>
              {(helpUnread.data ?? 0) > 0 ? (
                <span
                  data-testid="chat-help-unread"
                  className="l2f-tabular rounded-pill bg-accent px-1.5 py-0.5 font-mono text-[10px] font-bold text-on-accent"
                >
                  {helpUnread.data}
                </span>
              ) : null}
            </button>

            {/* Tier filter and search are COACH affordances only. A lifter gets
                a plain list with the relationship state. The tier control is a
                single-line select with the counts inline, so it can never wrap
                into an untidy stack of pills. */}
            {isCoach ? (
              <>
                <label className="flex items-center gap-2" data-testid="chat-tier">
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                    Show
                  </span>
                  <select
                    data-testid="chat-filter-tier"
                    aria-label="Filter chatters by tier"
                    value={filter}
                    onChange={(event) => setFilter(event.target.value as Filter)}
                    className={cn(
                      'h-9 min-w-0 flex-1 rounded-chat border bg-page px-2 text-[12px] font-bold uppercase tracking-[0.06em] text-ink',
                      filter === 'all' ? 'border-hairline' : 'border-accent',
                    )}
                  >
                    {(['all', 'paid', 'connected', 'inquiry', 'managed'] as const).map((entry) => (
                      <option key={entry} value={entry}>
                        {FILTER_LABEL[entry]} ({counts[entry]})
                      </option>
                    ))}
                  </select>
                </label>
                <input
                  type="search"
                  data-testid="chat-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search name or email"
                  className="h-9 w-full rounded-pill border border-hairline bg-page-subtle px-3 text-[13px] text-ink outline-none focus:border-accent"
                />
              </>
            ) : null}

            {filtered.length === 0 ? (
              <p className="text-[13px] text-ink-muted" data-testid="chat-empty">
                No conversations yet.
              </p>
            ) : (
              <ul className="flex flex-col gap-1" data-testid="chat-chatter-list">
                {filtered.map((thread) => (
                  <li key={thread.linkId}>
                    <button
                      type="button"
                      data-testid={`chat-chatter-${thread.userId}`}
                      aria-current={selected?.linkId === thread.linkId}
                      onClick={() => {
                        setHelpOpen(false);
                        setSelectedUserId(thread.userId);
                      }}
                      className={cn(
                        'flex w-full items-start gap-2.5 rounded-chat border px-3 py-2.5 text-left transition-all duration-150',
                        selected?.linkId === thread.linkId
                          ? 'border-accent bg-page-subtle shadow-float'
                          : 'border-hairline bg-surface hover:-translate-y-0.5 hover:border-accent hover:shadow-float',
                      )}
                    >
                      <ChatAvatar name={thread.displayName} />
                      <span className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-[14px] font-bold text-ink">{thread.displayName}</span>
                          {thread.unread > 0 ? (
                            <span
                              data-testid={`chat-chatter-unread-${thread.userId}`}
                              className="l2f-tabular rounded-pill bg-accent px-1.5 py-0.5 font-mono text-[10px] font-bold text-on-accent"
                            >
                              {thread.unread}
                            </span>
                          ) : null}
                        </span>
                        <span className="flex flex-wrap items-center gap-1.5">
                          <TierChip tier={thread.tier} isCoach={isCoach} />
                          {isCoach ? (
                            <span data-testid={`chat-chatter-owner-${thread.userId}`}>
                              <OwnerChip owned={thread.owned} ownerName={thread.ownerName} />
                            </span>
                          ) : null}
                        </span>
                        <span className="truncate text-[11px] text-ink-muted">
                          {thread.lastMessage ?? 'No messages yet'}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </aside>

        <main
          className="flex min-h-[520px] min-w-0 flex-col gap-3 rounded-chat-lg border border-hairline bg-surface p-3 shadow-float sm:p-4 lg:min-h-0"
          data-testid="chat-thread-pane"
        >
          {helpOpen ? (
            <HelpConversation />
          ) : selected === null ? (
            <div className="flex flex-1 items-center justify-center p-2">
              <EmptyState
                className="w-full max-w-[520px] rounded-chat-lg border-dashed bg-surface/60"
                title="Pick a chatter"
                body="Choose someone on the left to read the thread and answer them."
                icon={<ChatIcon className="h-8 w-8 text-accent" />}
              />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-chat border border-hairline bg-page-subtle px-3 py-2.5">
                <div className="flex min-w-0 items-center gap-2.5">
                  <ChatAvatar name={selected.displayName} size="md" />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-[15px] font-bold text-ink">{selected.displayName}</span>
                    <span className="truncate text-[11.5px] text-ink-muted">
                      {selected.email || 'no email on file'}
                      {selected.lastMessageAt ? ` · last message ${timeAgo(selected.lastMessageAt)}` : ''}
                    </span>
                  </div>
                </div>
                <span className="flex flex-wrap items-center gap-1.5">
                  <TierChip tier={selected.tier} isCoach={isCoach} />
                  {isCoach ? (
                    <OwnerChip owned={selected.owned} ownerName={selected.ownerName} withName />
                  ) : null}
                </span>
              </div>

              {/* 0030: reassign from the workspace, without leaving the thread. */}
              {isCoach && session.user?.internalCoachOf == null && contextRow ? (
                <div
                  className="flex flex-wrap items-end gap-2 rounded-chat border border-hairline bg-page-subtle px-3 py-2"
                  data-testid="chat-assign"
                >
                  <label className="flex min-w-[200px] flex-1 flex-col gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                      Managed by
                    </span>
                    <select
                      value={assignId}
                      onChange={(event) => setAssignId(event.target.value)}
                      data-testid="chat-assign-select"
                      className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
                    >
                      <option value="">You (the owner)</option>
                      {ownerTeam
                        .filter((coach) => coach.status === 'active')
                        .map((coach) => (
                          <option key={coach.coachId} value={coach.coachId}>
                            {coach.displayName}
                          </option>
                        ))}
                    </select>
                  </label>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={assignBusy}
                    data-testid="chat-assign-save"
                    onClick={() => void assignSelected()}
                  >
                    Save
                  </Button>
                </div>
              ) : null}

              {/* Below xl the context panel is a collapsed disclosure so it can
                  never push the composer around until the coach opens it. */}
              {helpOpen ? (
                /* 0037 — with help open the side of the conversation is about
                   LIFT2FIT, never about a client whose thread happens to be
                   selected: a programme summary is noise while asking for help. */
                <details
                  className="rounded-chat border border-hairline bg-page-subtle xl:hidden"
                  data-testid="chat-help-feedback-details"
                >
                  <summary className="cursor-pointer px-3 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink">
                    Rate Lift2Fit
                  </summary>
                  <div className="border-t border-hairline p-3">
                    <HelpFeedbackPanel testId="chat-help-feedback" />
                  </div>
                </details>
              ) : isCoach ? (
                <details
                  className="rounded-chat border border-hairline bg-page-subtle xl:hidden"
                  data-testid="chat-context-details"
                >
                  <summary className="cursor-pointer px-3 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink">
                    Their programme &amp; progress
                  </summary>
                  <div className="border-t border-hairline p-3">
                    <ChatContextPanel
                      displayName={selected.displayName}
                      tier={selected.tier}
                      row={contextRow}
                      loading={contextLoading}
                      error={rosterState.error}
                    />
                  </div>
                </details>
              ) : (
                <details
                  className="rounded-chat border border-hairline bg-page-subtle xl:hidden"
                  data-testid="chat-coach-details"
                >
                  <summary className="cursor-pointer px-3 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink">
                    Your coach
                  </summary>
                  <div className="border-t border-hairline p-3">
                    <LifterCoachCard coachId={selected.userId} testId="chat-coach-card-mobile" />
                  </div>
                </details>
              )}

              {isCoach && selected.tier === 'inquiry' && selected.requestCode && selected.connectable ? (
                <div
                  className="flex flex-col gap-2 rounded-chat border border-hairline bg-page-subtle px-3 py-2"
                  data-testid={`chat-connect-${selected.userId}`}
                >
                  <p className="text-[13px] text-ink">
                    {selected.displayName} asked to connect. Accept to link them, or decline with a note they will read.
                  </p>
                  <textarea
                    data-testid="chat-connect-message"
                    rows={2}
                    maxLength={2000}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder="Optional note with your answer"
                    className="l2f-chat-well rounded-chat px-3 py-2 text-[14px] text-ink outline-none"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" disabled={busy} data-testid="chat-accept" onClick={() => void decide('accept')}>
                      <CheckIcon />
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      data-testid="chat-decline"
                      onClick={() => void decide('decline')}
                    >
                      <CloseIcon />
                      Decline
                    </Button>
                  </div>
                </div>
              ) : null}

              {isCoach && selected.tier === 'inquiry' && selected.requestCode && !selected.connectable ? (
                <p
                  className="rounded-chat border border-hairline bg-page-subtle px-3 py-2 text-[13px] text-ink"
                  data-testid={`chat-not-connectable-${selected.userId}`}
                >
                  {selected.displayName} is already connected to another coach. You can keep chatting, but they must
                  disconnect before they can connect to you or buy one of your packs.
                </p>
              ) : null}

              {isCoach && selected.tier !== 'paid' && selected.requestCode === null && selected.connectable ? (
                <div
                  className="flex flex-wrap items-center gap-2 rounded-chat border border-hairline px-3 py-2"
                  data-testid={`chat-promote-${selected.userId}`}
                >
                  <SparkIcon className="text-accent" />
                  <span className="text-[13px] text-ink">Connected, not yet a client. Offer a pack:</span>
                  <select
                    data-testid="chat-pack-select"
                    value={packId}
                    onChange={(event) => setPackId(event.target.value)}
                    className="h-9 rounded-chat border border-hairline bg-page px-2 text-[13px] text-ink"
                  >
                    <option value="">Choose a pack…</option>
                    {packs.map((pack) => (
                      <option key={pack.packId} value={pack.packId}>
                        {pack.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy || packId === ''}
                    data-testid="chat-offer-pack"
                    onClick={() => void offerPack()}
                  >
                    Send pack offer
                  </Button>
                </div>
              ) : null}

              {/* LIFTER actions: no accept/decline, no pack offer — those are the
                  coach's. A lifter cancels a request or disconnects. */}
              {!isCoach ? (
                <div
                  className="flex flex-wrap items-center gap-2 rounded-chat border border-hairline bg-page-subtle px-3 py-2"
                  data-testid={`chat-lifter-actions-${selected.userId}`}
                >
                  {selected.tier === 'inquiry' ? (
                    <>
                      <span className="text-[13px] text-ink">
                        {selected.connectable
                          ? `Waiting for ${selected.displayName} to accept.`
                          : `You are connected to another coach, so ${selected.displayName} cannot connect you yet.`}
                      </span>
                      {selected.requestCode ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          data-testid="chat-cancel-request"
                          onClick={() => void cancelRequest()}
                        >
                          Cancel request
                        </Button>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <span className="text-[13px] text-ink">
                        {selected.tier === 'paid'
                          ? `Your pack with ${selected.displayName} is active.`
                          : `You are connected to ${selected.displayName}.`}
                      </span>
                      <Link
                        to="/my-coach"
                        className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                      >
                        {selected.tier === 'paid' ? 'Manage your pack' : 'Browse packs'}
                        <ArrowRightIcon />
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        data-testid="chat-disconnect"
                        onClick={() => void disconnect()}
                      >
                        Disconnect
                      </Button>
                    </>
                  )}
                </div>
              ) : null}

              <div className="min-h-0 flex-1" data-testid="chat-messages">
                <CoachChatPanel
                  clientId={isCoach ? selected.userId : null}
                  coachId={isCoach ? null : selected.userId}
                  peerName={selected.displayName}
                  emptyTitle={`No messages with ${selected.displayName} yet`}
                  emptyBody="Say hello — the thread is open from the moment they inquire."
                  onUnread={() => threadsState.reload()}
                  testId="chat-panel"
                  fill
                  hideHeading
                />
              </div>
            </>
          )}
          <LiveMessage message={notice} tone={tone} />
        </main>

        {selected !== null || helpOpen ? (
          <aside
            className="hidden min-h-0 flex-col gap-3 overflow-y-auto rounded-chat-lg border border-hairline bg-surface p-3 shadow-float xl:flex"
            data-testid={helpOpen ? 'chat-help-rail' : 'chat-context-rail'}
          >
            {helpOpen ? (
              <HelpFeedbackPanel testId="chat-help-feedback" />
            ) : isCoach ? (
              <ChatContextPanel
                displayName={selected.displayName}
                tier={selected.tier}
                row={contextRow}
                loading={contextLoading}
                error={rosterState.error}
              />
            ) : (
              /* The lifter's side of the same idea: the coach's card — rating,
                 packs, any live offer — with the review form in it, so rating a
                 coach never means leaving the conversation to find their page. */
              <LifterCoachCard coachId={selected.userId} />
            )}
          </aside>
        ) : null}
      </div>
    </div>
  );
}
